import {
  type CellData,
  type ColumnDef,
  type RowData,
} from '@tanstack/react-table';
import { useMemo } from 'react';

import { useControllableState } from '../../../../core/hooks/useControllableState.js';
import { useDataTableConfigElement } from '../../../hooks/useDataTableConfig.js';
import { isGroupColumnDef } from '../../hooks/useTable/datatable-typeguards.js';
import type { DataTableRowData } from '../../row-data-types.js';
import type { TDataTableFeatures } from '../types/features.types.js';
import type { DataTableOptions } from '../types/table.types.js';
import type {
  DataTableColumnVisibilityProps,
  DataTableColumnVisibilityState,
} from './column-visibility-types.js';

/**
 * Helper function that generates visibility state from original columns.
 * @internal
 */
function getColumnVisibilityFromOriginalColumns<
  TData extends RowData,
  TValue extends CellData = CellData,
>(
  columnDefs: readonly ColumnDef<TDataTableFeatures, TData, TValue>[],
): DataTableColumnVisibilityState {
  const visibilityState: DataTableColumnVisibilityState = {};

  for (const columnDef of columnDefs) {
    if (
      isGroupColumnDef<TDataTableFeatures, TData>(columnDef) &&
      columnDef.columns
    ) {
      const subColumnDefs = columnDef.columns;
      for (const subColumnDef of subColumnDefs) {
        if (subColumnDef.id) {
          visibilityState[subColumnDef.id] = true;
        }
      }
    }
    // In our scenarios, we enforce id's on everything, but based on the tanstack types,
    // they could still be undefined, hence the check here.
    else if (columnDef.id) {
      visibilityState[columnDef.id] = true;
    }
  }

  return visibilityState;
}

/**
 * Configuration hook for the DataTable ColumnVisibility feature.
 * @internal
 */
export function useColumnVisibility<TData extends DataTableRowData>(
  props: DataTableColumnVisibilityProps,
  options: DataTableOptions<Exclude<TData, unknown>>,
): void {
  const columnVisibilityConfig = useDataTableConfigElement('columnVisibility');

  const [columnVisibility, onColumnVisibilityChange] = useControllableState({
    value: props.columnVisibility ?? columnVisibilityConfig,
    defaultValue: props.defaultColumnVisibility,
    onChange: props.onColumnVisibilityChange,
  });

  // oxlint-disable-next-line react/immutability
  options.state = {
    ...options.state,
    columnVisibility,
  };

  // oxlint-disable-next-line react/immutability
  options.onColumnVisibilityChange = onColumnVisibilityChange;
  // oxlint-disable-next-line react/immutability
  options.originalColumnVisibility = useMemo(
    () =>
      props.defaultColumnVisibility ??
      getColumnVisibilityFromOriginalColumns(options.columns),
    [props.defaultColumnVisibility, options.columns],
  );
}
